import { useTranslation } from '@kgc/i18n';
import { Checkbox, cn, Input, Textarea, toast } from '@kgc/ui';
import { ChevronDown, ChevronRight, ExternalLink, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { TestFeature } from '../data/release-tests-data';
import { type TestResultData, useRetestFeature, useSaveTestResult } from '../hooks/useTestResults';
import { getLocalizedFeatureName, getLocalizedField } from '../utils/localized-field';
import { formatRelativeDate } from '../utils/relative-date';
import { TestHistoryPopover } from './TestHistoryPopover';

/**
 * Safely parse a stepResults JSON blob stored on the server-side test result.
 * Returns empty object on any parse error — silent failure is intentional
 * because the card still needs to render a usable UI when legacy rows or
 * corrupt data are encountered. The failure is logged once via `console.warn`
 * so it is visible in DevTools / Sentry.
 */
function parseStepResults(raw: string | null | undefined): Record<number, boolean> {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<number, boolean>;
    }
    console.warn('[release-hub] stepResults JSON was not an object, ignoring:', raw);
    return {};
  } catch (err) {
    console.warn('[release-hub] failed to parse stepResults JSON:', err);
    return {};
  }
}

interface TestFeatureCardProps {
  feature: TestFeature;
  featureIndex: number;
  version: string;
  existingResult?: TestResultData;
}

export function TestFeatureCard({
  feature,
  featureIndex,
  version,
  existingResult,
}: TestFeatureCardProps) {
  const { locale, t } = useTranslation();
  const saveMutation = useSaveTestResult();
  const retestMutation = useRetestFeature();

  const [lastSaved, setLastSaved] = useState<{ name: string; date: string } | null>(null);
  const [status, setStatus] = useState<'passed' | 'failed' | 'not_tested'>(
    existingResult?.status ?? 'not_tested'
  );
  const [comment, setComment] = useState(existingResult?.comment ?? '');
  const [stepResults, setStepResults] = useState<Record<number, boolean>>(() =>
    parseStepResults(existingResult?.stepResults)
  );
  const [stepsExpanded, setStepsExpanded] = useState(false);
  const [showRetest, setShowRetest] = useState(false);
  const [retestNote, setRetestNote] = useState('');

  // Sync from server
  useEffect(() => {
    if (existingResult) {
      setStatus(existingResult.status);
      setComment(existingResult.comment ?? '');
      setStepResults(parseStepResults(existingResult.stepResults));
    }
  }, [existingResult]);

  // Memoized because these recompute on every parent render and the feature
  // props object is stable across renders — avoids wasteful re-resolution.
  const featureName = useMemo(() => getLocalizedFeatureName(feature, locale), [feature, locale]);
  const expectedResult = useMemo(
    () => getLocalizedField(feature.expected, locale),
    [feature.expected, locale]
  );

  const hasCheckedSteps =
    feature.steps.length === 0 || Object.values(stepResults).some(v => v === true);

  // Status border color
  const statusBorderColor =
    status === 'passed'
      ? 'border-l-green-500'
      : status === 'failed'
        ? 'border-l-red-500'
        : 'border-l-muted-foreground/30';

  // Tester info
  const testerName = lastSaved?.name ?? existingResult?.testerName;
  const testerDate = lastSaved?.date ?? existingResult?.updatedAt;
  const isTested = status !== 'not_tested';

  // --- Save logic ---
  const handleSave = useCallback(
    (newStatus: 'passed' | 'failed' | 'not_tested') => {
      const previousStatus = status;
      setStatus(newStatus);
      saveMutation.mutate(
        {
          version,
          featureIndex,
          featureName,
          status: newStatus,
          comment: comment || null,
          stepResults: Object.keys(stepResults).length > 0 ? stepResults : null,
        },
        {
          onSuccess: data => {
            setLastSaved({ name: data.result.testerName, date: data.result.updatedAt });
          },
          onError: () => {
            // Roll back the optimistic status so the UI reflects server truth.
            setStatus(previousStatus);
            toast.error(t('releaseHub.testing.saveError'));
          },
        }
      );
    },
    [status, saveMutation, version, featureIndex, featureName, comment, stepResults, t]
  );

  const toggleStep = (stepIdx: number) => {
    setStepResults(prev => {
      const next = { ...prev, [stepIdx]: !prev[stepIdx] };
      scheduleSave(next);
      return next;
    });
  };

  // --- Auto-save: debounce 1.5s, blur, beforeunload ---
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef(false);

  const doSave = useCallback(
    (overrideSteps?: Record<number, boolean>) => {
      if (status === 'not_tested') return;
      pendingRef.current = false;
      saveMutation.mutate(
        {
          version,
          featureIndex,
          featureName,
          status,
          comment: comment || null,
          stepResults:
            Object.keys(overrideSteps ?? stepResults).length > 0
              ? (overrideSteps ?? stepResults)
              : null,
        },
        {
          onSuccess: data => {
            setLastSaved({ name: data.result.testerName, date: data.result.updatedAt });
          },
          onError: () => {
            // Auto-save failure is silent by default except for a toast.
            // Re-mark as pending so the next user interaction retries.
            pendingRef.current = true;
            toast.error(t('releaseHub.testing.saveError'));
          },
        }
      );
    },
    [version, featureIndex, featureName, status, comment, stepResults, saveMutation, t]
  );

  const scheduleSave = useCallback(
    (overrideSteps?: Record<number, boolean>) => {
      pendingRef.current = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => doSave(overrideSteps), 1500);
    },
    [doSave]
  );

  const flushSave = useCallback(() => {
    if (pendingRef.current) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      doSave();
    }
  }, [doSave]);

  useEffect(() => {
    const handleUnload = () => flushSave();
    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [flushSave]);

  return (
    <div
      className={cn(
        'rounded-lg border border-border transition-shadow hover:shadow-md',
        'border-l-4',
        statusBorderColor
      )}
    >
      {/* Main area */}
      <div className="p-4">
        {/* Top row: title area + action buttons */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            {/* Feature ID + module */}
            {(feature.featureId ?? feature.module) && (
              <div className="mb-0.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {feature.featureId}
                {feature.featureId && feature.module && ' · '}
                {feature.module}
              </div>
            )}
            {/* Title */}
            <div className="text-[15px] font-semibold text-foreground">{featureName}</div>
            {/* Nav link */}
            {feature.navPath && (
              <a
                href={`#${feature.navPath}`}
                className="mt-1 inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                <ExternalLink className="h-3 w-3" />
                {feature.navLabel ?? feature.navPath}
              </a>
            )}
          </div>

          {/* Pass / Fail buttons */}
          <div className="flex shrink-0 gap-1.5">
            <button
              onClick={() => handleSave(status === 'passed' ? 'not_tested' : 'passed')}
              disabled={!hasCheckedSteps || saveMutation.isPending}
              className={cn(
                'flex items-center gap-1 rounded-md border-[1.5px] px-3 py-1.5 text-xs font-semibold transition-all',
                status === 'passed'
                  ? 'border-green-500 bg-green-500 text-white shadow-[0_0_0_2px_rgba(22,163,74,0.3)]'
                  : 'border-green-200 bg-green-50 text-green-600 hover:bg-green-500 hover:text-white dark:border-green-800 dark:bg-green-950/30 dark:text-green-400 dark:hover:bg-green-600',
                (!hasCheckedSteps || saveMutation.isPending) && 'cursor-not-allowed opacity-40'
              )}
            >
              ✓ {t('releaseHub.testing.passBtn')}
            </button>
            <button
              onClick={() => handleSave(status === 'failed' ? 'not_tested' : 'failed')}
              disabled={!hasCheckedSteps || saveMutation.isPending}
              className={cn(
                'flex items-center gap-1 rounded-md border-[1.5px] px-3 py-1.5 text-xs font-semibold transition-all',
                status === 'failed'
                  ? 'border-red-500 bg-red-500 text-white shadow-[0_0_0_2px_rgba(220,38,38,0.3)]'
                  : 'border-red-200 bg-red-50 text-red-600 hover:bg-red-500 hover:text-white dark:border-red-800 dark:bg-red-950/30 dark:text-red-400 dark:hover:bg-red-600',
                (!hasCheckedSteps || saveMutation.isPending) && 'cursor-not-allowed opacity-40'
              )}
            >
              ✗ {t('releaseHub.testing.failBtn')}
            </button>
          </div>
        </div>

        {/* Meta row: tester, date, history — ALWAYS visible */}
        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          {/* Tester */}
          <span className="inline-flex items-center gap-1">
            {isTested && testerName ? (
              <>
                <span className="inline-flex h-[18px] w-[18px] items-center justify-center rounded-full bg-amber-600 text-[9px] font-semibold text-white">
                  {testerName.charAt(0).toUpperCase()}
                </span>
                {testerName}
              </>
            ) : (
              <span className="italic text-muted-foreground/60">
                {t('releaseHub.testing.notTestedYet')}
              </span>
            )}
          </span>

          {/* Date */}
          <span>{isTested && testerDate ? formatRelativeDate(testerDate, locale) : '—'}</span>

          {/* History badge */}
          <TestHistoryPopover
            version={version}
            featureIndex={featureIndex}
            historyCount={existingResult?.historyCount ?? 0}
            totalSteps={feature.steps.length}
          />

          {/* Retest needed indicator */}
          {status === 'failed' && (
            <span className="font-semibold text-red-500">
              ↻ {t('releaseHub.testing.retestNeeded')}
            </span>
          )}

          {/* Retest button */}
          {(status === 'passed' || status === 'failed') && (
            <button
              onClick={() => setShowRetest(true)}
              disabled={retestMutation.isPending}
              className="ml-auto flex items-center gap-1 text-muted-foreground hover:text-primary disabled:opacity-50"
            >
              <RefreshCw className="h-3 w-3" />
              {t('releaseHub.testing.retest')}
            </button>
          )}
        </div>
      </div>

      {/* Retest inline section */}
      {showRetest && (
        <div className="space-y-2 border-t border-border bg-accent/20 p-3">
          <Input
            placeholder={t('releaseHub.testing.retestNote')}
            value={retestNote}
            onChange={e => setRetestNote(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                setShowRetest(false);
                setRetestNote('');
              }}
              className="text-sm text-muted-foreground hover:text-primary"
            >
              {t('releaseHub.testing.retestCancel')}
            </button>
            <button
              onClick={() => {
                retestMutation.mutate(
                  { version, featureIndex, retestNote: retestNote || undefined },
                  {
                    onSuccess: () => {
                      if (debounceRef.current) clearTimeout(debounceRef.current);
                      pendingRef.current = false;
                      setStatus('not_tested');
                      setComment('');
                      setStepResults({});
                      setShowRetest(false);
                      setRetestNote('');
                      setLastSaved(null);
                    },
                    onError: () => {
                      toast.error(t('releaseHub.testing.retestError'));
                    },
                  }
                );
              }}
              disabled={retestMutation.isPending}
              className="text-sm text-muted-foreground hover:text-primary disabled:opacity-50"
            >
              {t('releaseHub.testing.retestConfirm')}
            </button>
          </div>
        </div>
      )}

      {/* Steps toggle */}
      {feature.steps.length > 0 && (
        <button
          onClick={() => setStepsExpanded(prev => !prev)}
          className="flex w-full items-center gap-1.5 border-t border-border px-4 py-2 text-xs text-primary hover:bg-accent/30"
        >
          {stepsExpanded ? (
            <ChevronDown className="h-3 w-3" />
          ) : (
            <ChevronRight className="h-3 w-3" />
          )}
          {stepsExpanded
            ? t('releaseHub.testing.hideSteps', { count: feature.steps.length })
            : t('releaseHub.testing.showSteps', { count: feature.steps.length })}
        </button>
      )}

      {/* Steps content */}
      {stepsExpanded && (
        <div className="border-t border-border px-4 pb-3">
          {feature.steps.map((step, sIdx) => {
            const stepText = getLocalizedField(step, locale);
            const isChecked = !!stepResults[sIdx];
            return (
              <label
                key={sIdx}
                className="flex items-start gap-2 border-b border-border/50 py-2 text-sm last:border-b-0"
              >
                <Checkbox
                  checked={isChecked}
                  onCheckedChange={() => toggleStep(sIdx)}
                  className="mt-0.5"
                />
                <span
                  className={cn(
                    'flex-1 leading-snug',
                    isChecked && 'text-muted-foreground/60 line-through'
                  )}
                >
                  {sIdx + 1}. {stepText}
                </span>
              </label>
            );
          })}
          {/* Expected result */}
          <div className="mt-1 rounded border-l-[3px] border-green-500 bg-green-50 px-2.5 py-1.5 text-xs text-green-800 dark:bg-green-950/20 dark:text-green-400">
            ✓ {t('releaseHub.testing.expected')}: {expectedResult}
          </div>
        </div>
      )}

      {/* Comment — ALWAYS visible */}
      <div className="border-t border-border px-4 py-2">
        <Textarea
          className={cn(
            'min-h-[36px] resize-none text-xs',
            comment &&
              status === 'failed' &&
              'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/20',
            comment &&
              status === 'passed' &&
              'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/20'
          )}
          placeholder={t('releaseHub.testing.comment')}
          value={comment}
          onChange={e => {
            setComment(e.target.value);
            scheduleSave();
          }}
          onBlur={flushSave}
          rows={1}
        />
      </div>
    </div>
  );
}
